import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import FdeSelect from "@/frontend/features/Fde/FdeSelect";
import ConnectorIcon from "@/frontend/features/Fde/data/ConnectorIcon";
import type { ForgeConnector } from "@/frontend/features/Fde/data/types";

interface Props {
  connectors: ForgeConnector[];
  copy: Record<string, string>;
  disabled: boolean;
  onSelect: (connector: ForgeConnector) => void;
}

export default function ConnectorCatalog({
  connectors,
  copy,
  disabled,
  onSelect,
}: Props) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const categories = useMemo(
    () =>
      Array.from(
        new Set(connectors.map((connector) => connector.category)),
      ).sort(),
    [connectors],
  );
  const visible = useMemo(
    () =>
      connectors.filter((connector) => {
        const matchesQuery = `${connector.name} ${connector.category}`
          .toLowerCase()
          .includes(query.toLowerCase());
        return (
          matchesQuery &&
          (category === "all" || connector.category === category)
        );
      }),
    [category, connectors, query],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 gap-2 border-b border-border p-3">
        <label className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={copy.search_connectors}
            aria-label={copy.search_connectors}
            className="pl-9"
          />
        </label>
        <FdeSelect value={category} onValueChange={setCategory} ariaLabel={copy.filter_category} options={[{ value: "all", label: copy.all_categories }, ...categories.map((item) => ({ value: item, label: item }))]} className="max-w-48" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {visible.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            {copy.no_connectors}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {visible.map((connector) => (
              <button
                key={connector.id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(connector)}
                className="group flex min-h-28 items-start gap-3 rounded-lg border border-border bg-background p-4 text-left transition hover:border-primary/40 hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ConnectorIcon
                  iconUrl={connector.icon_url}
                  className="size-10 rounded-lg border border-border bg-muted/50"
                  imageClassName="size-7"
                  fallbackClassName="size-5 text-muted-foreground"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {connector.name}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {connector.category}
                  </span>
                  <span className="mt-3 flex flex-wrap gap-1.5">
                    <span className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {connector.engine}
                    </span>
                    {connector.stream_count != null && (
                      <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {connector.stream_count} {copy.streams}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
